import { Component, inject } from '@angular/core';
import { HlAreaComponent } from './hl-area.component';
import { Neigungsstufe, trackByIndex } from 'src/app/data-model';
import { DataService, wuchsbezirkFilter } from 'src/app/data.service';
import { WeatherService } from '@luzula/luzula-lib';
import { TransformationService } from '@luzula/luzula-lib';
import { ToastrService } from 'ngx-toastr';

// Neigungsstufen-Grenzen für automatische Zuordnung
const NEIGUNGSSTUFEN_GRENZEN: { stufe: Neigungsstufe; minGrad: number; maxGrad: number }[] = [
	{ stufe: 'Ebene', minGrad: 0, maxGrad: 1 },
	{ stufe: 'schwächer geneigter Flachhang', minGrad: 1, maxGrad: 3 },
	{ stufe: 'stärker geneigter Flachhang', minGrad: 3, maxGrad: 7 },
	{ stufe: 'schwächer geneigter Lehnhang', minGrad: 7, maxGrad: 15 },
	{ stufe: 'stärker geneigter Lehnhang', minGrad: 15, maxGrad: 25 },
	{ stufe: 'schwächer geneigter Steilhang', minGrad: 25, maxGrad: 60 },
	{ stufe: 'stärker geneigter Steilhang', minGrad: 60, maxGrad: 90 }
];

@Component({
    standalone: false,
    selector: '',
    templateUrl: './naturraum.component.html',
    styleUrls: ['./hl-area.component.css'],
    
})
export class NaturraumComponent extends HlAreaComponent {
  trackByIndex = trackByIndex;
	wuchsbezirkFilter = wuchsbezirkFilter;
	weatherLoading = false;

	private weatherService = inject(WeatherService);
	private transformationService = inject(TransformationService);
	private toastr = inject(ToastrService);
	private dataService = inject(DataService);

	async fetchWeather(useCurrentWeather = false) {
		if (!this.data?.header?.koordinaten) {
			this.toastr.warning('Keine Koordinaten vorhanden');
			return;
		}

		const latlng = this.transformationService.from(this.data.header.koordinaten);
		if (!latlng || !latlng.lat || !latlng.lng) {
			this.toastr.warning('Ungültige Koordinaten');
			return;
		}

		this.weatherLoading = true;

		try {
			let weather;
			if (useCurrentWeather) {
				weather = await this.weatherService.getCurrentWeather(latlng.lat, latlng.lng);
			} else {
				// Historisches Wetter für Aufnahmedatum
				const datum = this.data.header?.aufnahme?.datum;
				if (!datum) {
					this.toastr.warning('Kein Aufnahmedatum vorhanden');
					this.weatherLoading = false;
					return;
				}
				const dateStr = this.weatherService.formatDateForApi(datum);
				weather = await this.weatherService.getHistoricalWeather(latlng.lat, latlng.lng, dateStr);
			}

			if (weather) {
				const description = this.weatherService.describeWeather(weather);
				this.data.extendedHeader.witterung = description.full;

				// Rohdaten speichern für spätere Neuinterpretation
				const config = this.weatherService.getConfig();
				this.data.extendedHeader.witterungRohdaten = {
					temperature: weather.temperature,
					precipitation: weather.precipitation,
					cloudCover: weather.cloudCover,
					weatherCode: weather.weatherCode,
					windSpeed: weather.windSpeed,
					windDirection: weather.windDirection,
					humidity: weather.humidity,
					precipitationDays: weather.precipitationLast3Days,
					daysUsed: config.soil.tage,
					fetchedAt: new Date().toISOString(),
					isCurrentWeather: useCurrentWeather
				};

				this.toastr.success('Witterung abgerufen');
			} else {
				this.toastr.error('Keine Wetterdaten verfügbar');
			}
		} catch (error) {
			console.error('[NaturraumComponent] Fehler beim Abrufen der Witterung:', error);
			this.toastr.error('Fehler beim Abrufen der Witterung');
		}

		this.weatherLoading = false;
	}

	/**
	 * Prüft ob aktuelle Neigungsstufe eine Ebene oder Rampe ist (≤ 3°)
	 * Bei diesen wird die Einbindung in die Nachbarschaft verwendet
	 */
	istEbeneOderRampe(): boolean {
		const stufe = this.data?.extendedHeader?.relief?.neigungsstufe;
		if (!stufe) return true; // Default: Ebene annehmen
		return stufe === 'Ebene' || stufe === 'schwächer geneigter Flachhang';
	}

	/**
	 * Prüft ob die Hangrichtung relevant ist (ab stärker geneigtem Flachhang > 3°)
	 */
	istHangmitRichtung(): boolean {
		const stufe = this.data?.extendedHeader?.relief?.neigungsstufe;
		if (!stufe) return false;
		return stufe !== 'Ebene' && stufe !== 'schwächer geneigter Flachhang';
	}

	/**
	 * Wird aufgerufen wenn sich die Neigungsstufe ändert
	 * Setzt automatisch einen passenden Grad-Wert (Mitte des Bereichs)
	 */
	onNeigungsstufeChange(stufe: string) {
		if (!stufe || !this.data?.extendedHeader?.relief) return;
		const neigungsstufe = stufe as Neigungsstufe;

		const grenzen = NEIGUNGSSTUFEN_GRENZEN.find(g => g.stufe === stufe);
		if (grenzen) {
			// Grad-Wert auf Mitte des Bereichs setzen
			this.data.extendedHeader.relief.neigungGrad = Math.round((grenzen.minGrad + grenzen.maxGrad) / 2);
		}

		// Hangrichtung löschen wenn nicht mehr relevant
		if (!this.istHangmitRichtung()) {
			this.data.extendedHeader.relief.hangrichtung = undefined;
		}
	}

	/**
	 * Wird aufgerufen wenn sich der Neigungsgrad ändert
	 * Passt automatisch die Neigungsstufe an
	 */
	onNeigungGradChange(grad: number) {
		if (grad === undefined || grad === null || !this.data?.extendedHeader?.relief) return;

		const passendeStufe = NEIGUNGSSTUFEN_GRENZEN.find(
			g => grad >= g.minGrad && grad <= g.maxGrad
		);

		if (passendeStufe) {
			this.data.extendedHeader.relief.neigungsstufe = passendeStufe.stufe;
		}
	}
}
